import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, input, output, signal } from '@angular/core';

import { UtilsService } from 'Angular/services/core/utils.service';

import { IconModule } from 'Angular/features/directives/icon/icon.module';
import { TooltipModule } from 'Angular/features/directives/popovers/tooltip/tooltip.module';

@Component({
    selector: 'ycn-agent-message-actions',
    templateUrl: './agent-message-actions.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, IconModule, TooltipModule],
})
export class AgentMessageActionsComponent {
    private readonly utilsService = inject(UtilsService);

    protected readonly contentElement = input.required<HTMLElement>();

    protected readonly deleteChat = output<void>();

    protected readonly showAlert = signal(false);

    protected readonly fadeOutAlert = signal(false);

    async copyAIResponse(): Promise<void> {
        const element = this.contentElement();
        if (element) {
            const contentCopied = await this._copyElementToClipboard(element);
            if (contentCopied && !this.showAlert()) {
                this.showAlert.set(true);
                setTimeout(() => {
                    this.fadeOutAlert.set(true);
                }, 1000);
                setTimeout(() => {
                    this.showAlert.set(false);
                    this.fadeOutAlert.set(false);
                }, 1300);
            }
        }
    }

    private async _copyElementToClipboard(element: HTMLElement): Promise<boolean> {
        if (navigator.clipboard && typeof ClipboardItem !== 'undefined') {
            try {
                await navigator.clipboard.write([
                    new ClipboardItem({
                        'text/html': new Blob([this._toClipboardHtml(element)], { type: 'text/html' }),
                        'text/plain': new Blob([element.innerText], { type: 'text/plain' }),
                    }),
                ]);
                return true;
            } catch {
                // Fall through to the plain-text helper (e.g. the browser rejects the text/html flavor)
            }
        }
        return this.utilsService.copyToClipboard(element.innerText);
    }

    private _toClipboardHtml(element: HTMLElement): string {
        // A <template> keeps the parsed copy inert, so nothing in it can load or run.
        const template = document.createElement('template');
        template.innerHTML = element.innerHTML;
        Array.from(template.content.querySelectorAll('a[href]')).forEach((anchor) => {
            const href = anchor.getAttribute('href');
            if (href) {
                try {
                    anchor.setAttribute('href', new URL(href, window.location.href).toString());
                } catch {
                    // Leave an unresolvable href (e.g. "https://") as-is rather than failing the copy
                }
            }
        });
        return template.innerHTML;
    }
}
